/**
 * Utility-Funktionen für Cloud Function URLs
 * Bestimmt automatisch die richtige Umgebung basierend auf Environment Variables
 */

import { getAuth } from 'firebase/auth';

/**
 * Gibt die Base URL für Cloud Functions basierend auf der aktuellen Umgebung zurück
 * @returns {string} Die Base URL für Cloud Functions
 */
let _cachedProject = null;

const getCloudFunctionProject = () => {
  if (_cachedProject) return _cachedProject;

  const environment = process.env.REACT_APP_ENV;
  const projectId = process.env.REACT_APP_PROJECT_ID;

  const isDev = environment === 'development' || environment === 'dev'
    || projectId === 'bonusclub-dev' || projectId === 'bonusclub-dev-461411';

  _cachedProject = isDev ? 'bonusclub-dev' : 'bonusclub-ab5e2';

  console.log('🌍 [CLOUD-FUNCTIONS] Environment:', environment, '| Project:', projectId, '| Functions:', _cachedProject);
  return _cachedProject;
};

/**
 * Endpunkte, deren Verarbeitung nach Europa verlegt ist. Der Rest läuft weiter
 * in us-central1, deshalb hängt die Region an der einzelnen Function und nicht
 * an der Base-URL — ein globaler Umzug würde alle noch nicht verschobenen
 * Endpunkte ins Leere zeigen lassen.
 */
const EU_FUNCTIONS = new Set([
  'fetchEmailContent',
  'downloadEmailAttachment',
  'sendWorldEmail',
  'cancelScheduledEmail',
  'sendScheduledEmailNow',
  'getScheduledEmails',
  'encryptEmailPasswordServer',
  'worldEmailCreator',
  'generateSubjectLines',
  'generateWritingSparks',
  'generateTemplateAI',
  'analyzeSpamRisk',
  'validateVerifyEmail',
  'dmEmailFinder',
  'extractEmailsFromDocument',
  'googleReviewAudit',
  'dataConnector',
]);

export const getCloudFunctionBaseUrl = () =>
  `https://us-central1-${getCloudFunctionProject()}.cloudfunctions.net`;

/**
 * Erstellt die vollständige URL für eine Cloud Function
 * @param {string} functionName - Name der Cloud Function
 * @returns {string} Vollständige URL zur Cloud Function
 */
export const getCloudFunctionUrl = (functionName) => {
  const region = EU_FUNCTIONS.has(functionName) ? 'europe-west1' : 'us-central1';
  return `https://${region}-${getCloudFunctionProject()}.cloudfunctions.net/${functionName}`;
};

export const clientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';
  } catch {
    return 'Europe/Berlin';
  }
};

const DATE_AWARE_FUNCTION_RE = /\/(worldEmailCreator|generateSubjectLines)$/;

function withClientTimezone(url, options = {}) {
  if (!DATE_AWARE_FUNCTION_RE.test(url) || typeof options.body !== 'string') return options;
  try {
    const parsed = JSON.parse(options.body);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || parsed.timezone) {
      return options;
    }
    return { ...options, body: JSON.stringify({ ...parsed, timezone: clientTimezone() }) };
  } catch {
    return options;
  }
}

/**
 * Authentifizierter Fetch-Wrapper für Cloud Function Aufrufe.
 * Fügt automatisch den Firebase Auth Token als Authorization Header hinzu.
 * 
 * @param {string} url - Die URL der Cloud Function
 * @param {object} [options={}] - Standard fetch() Optionen (method, body, headers, etc.)
 * @returns {Promise<Response>} - Die fetch Response
 * @throws {Error} - Wenn kein User eingeloggt ist
 */
export const authenticatedFetch = async (url, options = {}) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User is not authenticated. Please log in first.');
  }

  const requestOptions = withClientTimezone(url, options);

  const buildHeaders = (token) => ({
    ...requestOptions.headers,
    'Authorization': `Bearer ${token}`,
  });

  let idToken = await user.getIdToken();
  let response = await fetch(url, {
    ...requestOptions,
    headers: buildHeaders(idToken),
  });

  // Expired/stale tokens: one forced refresh + retry (fixes many 401s on long sessions)
  if (response.status === 401) {
    idToken = await user.getIdToken(true);
    response = await fetch(url, {
      ...requestOptions,
      headers: buildHeaders(idToken),
    });
  }

  return response;
};

/**
 * Vordefinierte URLs für häufig verwendete Cloud Functions
 */
export const CLOUD_FUNCTION_URLS = {
  sendWorldEmail: () => getCloudFunctionUrl('sendWorldEmail'),
  worldEmailCreator: () => getCloudFunctionUrl('worldEmailCreator'),
  generateDynamicImage: () => getCloudFunctionUrl('generateDynamicImage'),
  miniApp: () => getCloudFunctionUrl('miniApp'),
  cleanupContact: () => getCloudFunctionUrl('cleanupContact'),
  validateVerifyEmail: () => getCloudFunctionUrl('validateVerifyEmail'),
  dmEmailFinder: () => getCloudFunctionUrl('dmEmailFinder'),
  testEmailConnection: () => getCloudFunctionUrl('testEmailConnection'),
  fetchEmailContent: () => getCloudFunctionUrl('fetchEmailContent'),
  cancelScheduledEmail: () => getCloudFunctionUrl('cancelScheduledEmail'),
  sendScheduledEmailNow: () => getCloudFunctionUrl('sendScheduledEmailNow'),
  getEmailSettings: () => getCloudFunctionUrl('getEmailSettings'),
  updateEmailSettings: () => getCloudFunctionUrl('updateEmailSettings'),
  updateSenderDelay: () => getCloudFunctionUrl('updateSenderDelay'),
  worldWebCrawler: () => getCloudFunctionUrl('worldWebCrawler'),
  newWorldWebCrawler: () => getCloudFunctionUrl('newWorldWebCrawler'),
  fetchInbox: () => getCloudFunctionUrl('fetchInbox'),
  deleteEmail: () => getCloudFunctionUrl('deleteEmail'),
  markEmailAsRead: () => getCloudFunctionUrl('markEmailAsRead'),
  hotelFetcher: () => getCloudFunctionUrl('hotelFetcher'),
  // Turbo AI Setup Functions
  turboAiSetup: () => getCloudFunctionUrl('turboAiSetup'),
  turboAiAnalyze: () => getCloudFunctionUrl('turboAiAnalyze'),
  updateAiSettings: () => getCloudFunctionUrl('updateAiSettings'),

  // 🚀 Ultimate Setup — modular pipeline (Opus 4.7 + Thinking)
  setupAnalyzeProfile: () => getCloudFunctionUrl('setupAnalyzeProfile'),
  setupWriteVault: () => getCloudFunctionUrl('setupWriteVault'),
  setupGenerateCleaner: () => getCloudFunctionUrl('setupGenerateCleaner'),
  setupGenerateSafeGuard: () => getCloudFunctionUrl('setupGenerateSafeGuard'),
  setupDesignTemplateStrategy: () => getCloudFunctionUrl('setupDesignTemplateStrategy'),
  setupGenerateVariants: () => getCloudFunctionUrl('setupGenerateVariants'),
  setupRefineVariants: () => getCloudFunctionUrl('setupRefineVariants'),
  setupPersistSetup: () => getCloudFunctionUrl('setupPersistSetup'),
  setupGenerateLeads: () => getCloudFunctionUrl('setupGenerateLeads'),
  setupRunAll: () => getCloudFunctionUrl('setupRunAll'),
  setupResumeRun: () => getCloudFunctionUrl('setupResumeRun'),
  
  // AI Page Generation Functions (2025 System)
  createAiPage: () => getCloudFunctionUrl('createAiPage'),
  
  // 🎬 YouTube Storyteller - Grok AI Integration (2025)
  generateYoutubeStoryIdeas: () => getCloudFunctionUrl('generateYoutubeStoryIdeas'),
  generateYoutubeScript: () => getCloudFunctionUrl('generateYoutubeScript'),
  
  // 🔐 Server-side Password Encryption
  encryptEmailPasswordServer: () => getCloudFunctionUrl('encryptEmailPasswordServer'),
  
  // ✨ AI Template Content Generation
  generateTemplateAI: () => getCloudFunctionUrl('generateTemplateAI'),

  // 💎 Value-Based Offer Generation (Claude Opus)
  generateValueBasedOffer: () => getCloudFunctionUrl('generateValueBasedOffer'),
  prepareValueBasedOfferIntake: () => getCloudFunctionUrl('prepareValueBasedOfferIntake'),
  researchCompanyForOffer: () => getCloudFunctionUrl('researchCompanyForOffer'),

  // 🎯 Potential Analyser ("KI-Qualifyer") — 1-10 ICP fit score per contact
  analyzePotential: () => getCloudFunctionUrl('analyzePotential'),
  
  // Instagram Research (proxy)
  instagramResearch: () => getCloudFunctionUrl('instagramResearch'),

  // Google Review Audit (own Maps scraper + LLM)
  googleReviewAudit: () => getCloudFunctionUrl('googleReviewAudit'),

  // Custom researchers (external platform APIs)
  dataConnector: () => getCloudFunctionUrl('dataConnector'),

  // SEO Analyzer (Sistrix proxy)
  sistrixProxy: () => getCloudFunctionUrl('sistrixProxy'),
  
  // Writing Sparks (side panel suggestions)
  generateWritingSparks: () => getCloudFunctionUrl('generateWritingSparks'),
  
  // Subject Line Generator (4 alternatives)
  generateSubjectLines: () => getCloudFunctionUrl('generateSubjectLines'),

  // Intelligent Spam Analyzer (holistic AI spam-risk check for the composer)
  analyzeSpamRisk: () => getCloudFunctionUrl('analyzeSpamRisk'),
  
  // Screen Composer (screengrab.cloud proxy)
  screenComposerProxy: () => getCloudFunctionUrl('screenComposerProxy'),

  // Website Analyzer (xSpyyy CMS/shop/age scanner)
  websiteAnalyzer: () => getCloudFunctionUrl('websiteAnalyzer'),

  // PDF Reference File (Hyper-Intelligence — Claude Files API)
  uploadReferenceFile: () => getCloudFunctionUrl('uploadReferenceFile'),
  deleteReferenceFile: () => getCloudFunctionUrl('deleteReferenceFile'),
  extractEmailsFromDocument: () => getCloudFunctionUrl('extractEmailsFromDocument'),

  // 🎙️ Voice Memos for Notes & Files (Cockpit Data Vault)
  transcribeVoiceMemoChunk: () => getCloudFunctionUrl('transcribeVoiceMemoChunk'),
  // Schema-driven structuring endpoint used by the global SpeakEngine (Notes & Files,
  // Ultimate Setup, and any future surface). Replaces the old structureVoiceMemoNotes.
  structureVoiceMemoAny: () => getCloudFunctionUrl('structureVoiceMemoAny'),

  // CRM Domain Guard — Close integration (Admin → CRM Integrations)
  crmDomainCheck: () => getCloudFunctionUrl('crmDomainCheck'),
  crmConnectClose: () => getCloudFunctionUrl('crmConnectClose'),
  crmSaveCloseConfig: () => getCloudFunctionUrl('crmSaveCloseConfig'),
  crmDisconnect: () => getCloudFunctionUrl('crmDisconnect'),
  crmTestDomain: () => getCloudFunctionUrl('crmTestDomain'),
}; 